import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button, Card, Col, Layout, Row, Typography } from "antd";
import { CheckCircleOutlined, FilePdfOutlined, SendOutlined, WhatsAppOutlined } from "@ant-design/icons";
import CustomHeader from "../Header";
import Footer from "../Footer";
import AutoScrollingRow from "./SalesCardScroller";
import "./SalesFeature.css";
import { salesAdvantages, salesDashboardFeatures, SalesFeatureText, salesTargetAudience, salesTemplates } from "./salesFeatureConstant";
import { scrollToElementById } from "../../../Utility/scrollUtils";
import { Helmet } from "react-helmet-async";

const { Header, Content } = Layout;
const { Title, Paragraph } = Typography;

const SalesFeature: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    scrollToElementById("sales");
  }, [pathname]);

  return (
    <div id="sales">
      <Helmet>
        <title>Sales Management Software to Boost Revenue Growth | Ease Margin</title>
        <meta
          name="description"
          content="Streamline your sales process with powerful sales management software. Track leads, manage pipelines, and improve conversions with EaseMargin."
        />
        <link
          rel="canonical" href="https://easemargin.com/services/sales-management" />
      </Helmet>

      <CustomHeader />
      <Layout className="salesFeature-layout">
        <Header className="salesFeature-header">
          <div className="salesFeature-header-inner">
            <Title level={1} className="salesFeature-title-large">
              {SalesFeatureText.HERO_TITLE}
            </Title>
            <Paragraph className="salesFeature-paragraph-header">
              {SalesFeatureText.HERO_DESCRIPTION}
            </Paragraph>
            <Link to="/requestdemo">
              <Button type="primary" size="large" className="btn-orange">
                Request a Demo
              </Button>
            </Link>
          </div>
        </Header>
        <Content className="salesFeature-content">
          <Title level={2} className="salesFeature-title-2nd">
            {SalesFeatureText.INTRO_TITLE}
          </Title>
          <Paragraph className="salesFeature-paragraph-main">
            {SalesFeatureText.INTRO_DESCRIPTION}
          </Paragraph>
          <Title level={3} className="salesFeature-title-3rd">
            {SalesFeatureText.SALES_FLOW_TITLE}
          </Title>
          <AutoScrollingRow />
          <Title level={3} className="salesFeature-title-3rd-marginTop">
            {SalesFeatureText.MULTI_FORMAT_TITLE}
          </Title>
          <Row gutter={[24, 24]} style={{ marginTop: 20 }}>
            <Col xs={24} md={12}>
              <Card bordered={false} className="card-default">
                <Title level={4} style={{ color: "#722ed1" }}>
                  {SalesFeatureText.DOC_FORMAT}
                </Title>
                <Row gutter={[24, 24]}>
                  <Col span={12}>
                    <Paragraph style={{ marginLeft: 20 }}>
                      <FilePdfOutlined
                        style={{ marginRight: 8, color: "#fa541c" }}
                      />
                      PDF
                    </Paragraph>
                    <Paragraph style={{ marginLeft: 20 }}>
                      🖨️ Printable A4
                    </Paragraph>
                    <Paragraph style={{ marginLeft: 20 }}>
                      ✉️ Email-ready attachments
                    </Paragraph>
                  </Col>
                </Row>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card bordered={false} className="card-default">
                <Title level={4} style={{ color: "#722ed1" }}>
                  {SalesFeatureText.SHARING_STORE}
                </Title>
                <Row gutter={[0, 24]}>
                  <Col span={12}>
                    <Paragraph style={{ marginLeft: 20 }}>
                      <FilePdfOutlined
                        style={{ marginRight: 8, color: "#f5222d" }}
                      />
                      Download
                    </Paragraph>
                    <Paragraph style={{ marginLeft: 20 }}>
                      🖨️ Print directly
                    </Paragraph>
                    <Paragraph style={{ marginLeft: 20 }}>
                      <SendOutlined
                        style={{ marginRight: 8, color: "#1890ff" }}
                      />
                      Send via Email
                    </Paragraph>
                  </Col>
                  <Col span={12}>
                    <Paragraph style={{ marginLeft: 20 }}>
                      <WhatsAppOutlined
                        style={{ marginRight: 8, color: "#25D366" }}
                      />
                      Send via WhatsApp
                    </Paragraph>
                    <Paragraph style={{ marginLeft: 20 }}>
                      🔗 Share Document Links
                    </Paragraph>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
          <Row gutter={[24, 24]} style={{ marginTop: 40 }}>
            {salesTemplates.map((feature, index) => (
              <Col xs={24} md={12} key={index}>
                <Card bordered={false} className="card-default">
                  <Title level={4} className="salesFeature-title-flex">
                    <span style={{ marginRight: 8 }}>{feature.icon}</span>
                    {feature.title}
                  </Title>
                  <Paragraph className="salesFeature-paragraph-desc">
                    {feature.description}
                  </Paragraph>
                </Card>
              </Col>
            ))}
          </Row>
          <Title level={3} className="salesFeature-title-3rd-marginTop">
            {SalesFeatureText.DASHBOARD_TITLE}
          </Title>
          <Row gutter={[24, 24]} style={{ marginBottom: 40 }}>
            {salesDashboardFeatures.map((feature, index) => (
              <Col xs={24} sm={12} key={index}>
                <Card bordered={false} className="card-default" style={{ backgroundColor: "#F6FFED" }}>
                  <Paragraph className="salesFeature-paragraph-dashboard">
                    <CheckCircleOutlined
                      style={{ color: "#1890ff", marginRight: 8 }}
                    />
                    {feature}
                  </Paragraph>
                </Card>
              </Col>
            ))}
          </Row>
          <Title level={3} className="salesFeature-title-3rd-marginTop">
            {SalesFeatureText.ADVANTAGE_TITLE}
          </Title>
          <Row gutter={[16, 16]} style={{ marginBottom: 40 }}>
            {salesAdvantages.map((advantage, idx) => (
              <Col xs={24} sm={12} key={idx}>
                <Card bordered={false} className="card-default card-light" style={{ backgroundColor: "#F6FFED" }}>
                  <Paragraph className="salesFeature-paragraph-dashboard">
                    <CheckCircleOutlined
                      style={{ color: "#1890ff", marginRight: 8 }}
                    />
                    {advantage}
                  </Paragraph>
                </Card>
              </Col>
            ))}
          </Row>
          <Title level={3} className="salesFeature-title-3rd-marginTop">
            {SalesFeatureText.PERFECT_FOR_TITLE}
          </Title>
          <Row gutter={[16, 16]}>
            {salesTargetAudience.map((target, idx) => (
              <Col xs={24} sm={12} key={idx}>
                <Card bordered={false} className="card-default card-light" style={{ backgroundColor: "#FFF1F0" }}>
                  <Paragraph className="salesFeature-paragraph-targetAudience">
                    <CheckCircleOutlined
                      style={{ color: "#1890ff", marginRight: 8 }}
                    />
                    {target}
                  </Paragraph>
                </Card>
              </Col>
            ))}
          </Row>
          <Card
            bordered={false}
            className="card-default salesFeature-card-footer"
          >
            <Title level={2} className="salesFeature-title-footer">
              {SalesFeatureText.CTA_TITLE}
            </Title>
            <Paragraph className="salesFeature-paragraph-footer">
              {SalesFeatureText.CTA_DESCRIPTION}
            </Paragraph>
            <Link to="/requestdemo">
              <Button type="primary" size="large" className="btn-orange">
                {SalesFeatureText.CTA_BUTTON}
              </Button>
            </Link>
          </Card>
        </Content>
      </Layout>
      <Footer />
    </div>
  );
};

export default SalesFeature;
